import { Reducer } from 'redux';
import {
    OpenSubmissionsElement,
    OpenSubmissionsDataElement,
    OpenSubmissionsStreamElement,
} from 'communication/types/openSubmissions/OpenSubmissionsElement';
import { StreamMessageElement } from 'communication/types/StreamElement';
import { StreamElementType } from 'communication/types/StreamElementType';
import streamMessagesReducer from 'utils/messages/StreamMessage';
import keyedStreamReducer from 'utils/redux/keyedStreamReducer';
import {
    createStreamReducer,
    createProcessStreamDataMessage,
} from 'widgets/store/stream/reducer';
import { WidgetStreamAction } from 'widgets/store/stream/actions';
import { WidgetStreamState } from 'widgets/store/stream/WidgetStreamState';
import { mapToOpenSubmissionsStreamItemFromElement } from './mapping';
import {
    initialOpenSubmissionsStreamState,
    OpenSubmissionsDataState,
    OpenSubmissionsStreamState,
    OpenSubmissionsStreamItem,
} from './OpenSubmissionsStreamDataState';
import {
    OpenSubmissionsWidgetStreamAction,
    OpenSubmissionsStreamActionType,
} from './actions';

const handleStreamMessageReceive = (
    element: OpenSubmissionsDataElement,
    nextState: OpenSubmissionsStreamState
) => {
    const nextDataState = nextState.data;

    streamMessagesReducer(element as StreamMessageElement, nextState);

    const keyedElement = mapRemoveElementToUpdateElement(
        element as OpenSubmissionsStreamElement<StreamElementType>
    );

    nextDataState.submissions = keyedStreamReducer(
        keyedElement,
        nextDataState.submissions,
        mapToOpenSubmissionsStreamItemFromElement
    );
};

const streamActionReducer = createStreamReducer<
    OpenSubmissionsDataState,
    OpenSubmissionsElement
>(
    initialOpenSubmissionsStreamState,
    createProcessStreamDataMessage(handleStreamMessageReceive)
);

type OpenSubmissionsStreamAction =
    | WidgetStreamAction<OpenSubmissionsElement>
    | OpenSubmissionsWidgetStreamAction;

export const openSubmissionsStreamReducer: Reducer<
    WidgetStreamState<OpenSubmissionsDataState>,
    OpenSubmissionsStreamAction
> = (
    state: WidgetStreamState<OpenSubmissionsDataState> = initialOpenSubmissionsStreamState,
    action: OpenSubmissionsStreamAction
) => {
    const submissions = state.data.submissions;
    switch (action.type) {
        case OpenSubmissionsStreamActionType.OPEN_SUBMISSIONS_START_FADE_OUT:
            return getNextState(
                state,
                submissions.map((value: OpenSubmissionsStreamItem) => {
                    if (value._i !== action.payload!.submissionKey) {
                        return value;
                    }
                    return { ...value, isFading: true };
                })
            );
        case OpenSubmissionsStreamActionType.OPEN_SUBMISSIONS_REMOVE_SUBMISSION:
            return getNextState(
                state,
                submissions.filter(
                    (e) => e._i !== action.payload!.submissionKey
                )
            );
        default:
            return streamActionReducer(state, action);
    }
};

function mapRemoveElementToUpdateElement(
    element: OpenSubmissionsStreamElement<StreamElementType>
): OpenSubmissionsStreamElement<StreamElementType> {
    return element._c === StreamElementType.Remove
        ? { ...element, _c: StreamElementType.Update, shouldRemove: true }
        : element;
}

function getNextState(
    state: WidgetStreamState<OpenSubmissionsDataState>,
    submissions: OpenSubmissionsStreamItem[]
) {
    return {
        ...state,
        data: {
            submissions,
        },
    };
}
